import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Scale } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchAllLegal, type AdminLegalRequest } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

/**
 * A19.13 — Shaffoflik hisoboti. GET /admin/legal (barcha statuslar) ustidan
 * holat + tur taqsimoti (client-side agregat). PII-siz jamlanma.
 */
export function LegalTransparency() {
  const t = useT()
  const { can } = useAuth()
  const [rows, setRows] = useState<AdminLegalRequest[] | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    fetchAllLegal(ac.signal)
      .then((r) => alive && setRows(r))
      .catch(() => {})
    return () => {
      alive = false
      ac.abort()
    }
  }, [])

  const agg = useMemo(() => {
    const r = rows ?? []
    const by = (p: (x: AdminLegalRequest) => boolean) => r.filter(p).length
    const kinds = new Map<string, number>()
    for (const x of r) kinds.set(x.kind, (kinds.get(x.kind) ?? 0) + 1)
    return {
      total: r.length,
      pending: by((x) => x.status === 'pending'),
      fulfilled: by((x) => x.status === 'fulfilled'),
      rejected: by((x) => x.status === 'rejected'),
      byKind: [...kinds.entries()].sort((a, b) => b[1] - a[1]),
    }
  }, [rows])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />
  const loading = rows === null

  const kindLabel: Record<string, string> = {
    data: t.legal.data,
    preservation: t.legal.preservation,
    removal: t.legal.removal,
    blocking: t.legal.blocking,
    emergency: t.legal.emergency,
  }

  return (
    <>
      <PageHeader
        title={t.legal.transparencyTitle}
        screenId="A19.13"
        actions={
          <Link
            to="/legal"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.legal.queue}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mb-4 flex flex-wrap gap-3">
          <Tile label={t.legal.total} value={agg.total} loading={loading} icon={<Scale size={18} className="text-accent" />} />
          <Tile label={t.mod.pending} value={agg.pending} loading={loading} />
          <Tile label={t.legal.fulfilled} value={agg.fulfilled} loading={loading} tone="success" />
          <Tile label={t.legal.rejected} value={agg.rejected} loading={loading} tone="danger" />
        </div>
        <div className="max-w-md overflow-hidden rounded-md border border-border-default">
          <div className="border-b border-border-default bg-surface-2 px-3 py-2 text-xs font-medium text-tertiary">
            {t.legal.byKind}
          </div>
          {agg.byKind.length === 0 && !loading ? (
            <div className="px-3 py-4 text-center text-sm text-tertiary">—</div>
          ) : (
            agg.byKind.map(([kind, count]) => (
              <div key={kind} className="flex items-center justify-between border-b border-border-subtle px-3 py-2 text-sm last:border-0">
                <span className="text-secondary">{kindLabel[kind] ?? kind}</span>
                <span className="font-mono tabular text-primary">{count}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  )
}

function Tile({
  label,
  value,
  loading,
  tone,
  icon,
}: {
  label: string
  value: number
  loading: boolean
  tone?: 'success' | 'danger'
  icon?: ReactNode
}) {
  const color = tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-primary'
  return (
    <div className="inline-flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
      {icon}
      <div>
        <div className="text-xs text-tertiary">{label}</div>
        {loading ? <Skeleton className="mt-1 h-6 w-12" /> : <div className={`font-mono text-xl tabular ${color}`}>{value.toLocaleString()}</div>}
      </div>
    </div>
  )
}
